/* The product guide — one registry, four consumers:
     1. GuidePanel renders these topics contextually (per route) in the app.
     2. HelpDot anchors open a specific topic from any card header.
     3. The command bar derives its suggestion chips from each topic's
        `examples` for the current route (falling back to its built-ins).
     4. scripts/sync-guide-knowledge.mjs upserts the same topics into the
        Firestore `knowledge` corpus so the chat assistant and command bar
        can answer "how do I…?" questions with this exact content.
   Keep this file pure data (no JSX, no CRA imports) so the sync script can
   import it from Node. Bodies are markdown-lite: paragraphs split on blank
   lines, `- ` bullets, **bold**, `code`.

   `examples` are copy-pasteable agent requests: { verb: 'ask' | 'do', text }.
   'ask' goes to the project brain; 'do' is a drafting-agent instruction.
   GuidePanel renders them as Try-it chips that prefill the command bar. */

export const GUIDE_TOPICS = [
  {
    id: 'getting-started',
    emoji: '🚀',
    title: 'Your first project — a 5-minute walkthrough',
    routes: ['^/$', '^/projects'],
    keywords: ['getting started', 'first project', 'walkthrough', 'tutorial', 'begin', 'new user', 'onboarding'],
    body: `Everything in fast.legal happens inside a **project**. Here is the whole working loop, end to end:

1. **Create a project** (Projects → New project). Pick the type that matches the matter — negotiated agreement, memo, formation, deck — and tag it with the company name.
2. **Add the first document** — upload a Word/PDF/Excel file or paste an email thread into the **Ask, instruct & add** panel (the **Add** verb). Ingest splits it into clauses, parses key facts, and indexes the project brain. If it's a negotiated agreement, smart import suggests deal points for the register.
3. **Ask before you act** — press **⌘K** and ask "what is still open?" or "summarize this draft's risk for us". Answers cite exact clauses.
4. **Instruct the agent** — switch the panel to **Instruct** and describe the edit in plain language. The agent files a reviewable proposal; nothing publishes until you approve it.
5. **Send work out** — Word/PDF exports from any document, or **Export package** for the full response bundle (clean + redline + open points).

The dashboard's four **job cards** (Open the draft · Review what moved · Deal points · Send a package) are the fastest routes back into steps 3–5 every day after.

If you get lost: every card has a small **?** that opens this guide, and the assistant behind ⌘K answers "how do I…" questions in your own words.`,
    examples: [
      { verb: 'ask', text: 'Summarize this project — documents, parties, and what is still open' },
      { verb: 'ask', text: 'How do I upload a new draft from the counterparty?' },
      { verb: 'ask', text: 'What should I look at first on this project?' },
    ],
  },
  {
    id: 'projects',
    emoji: '🗂️',
    title: 'Projects & the sidebar',
    routes: ['^/$', '^/projects'],
    keywords: ['project', 'sidebar', 'tags', 'search', 'create'],
    body: `Everything in fast.legal lives inside a **project** — an agreement negotiation, a presentation, a memo, a filing set. The home page lists every project you have access to; the left sidebar keeps your recent ones a click away.

- **Search and filter** by name or #tags (company, document type, deal stage) to find all artifacts for a given company, then narrow by type.
- **Create** in the sidebar makes anything, from anywhere: a new project, an AI-drafted document, a presentation, an Excel workbook, or a file upload — project-scoped kinds ask which project if you are not already in one.
- The sidebar's **Library** section cuts across projects by artifact kind — Documents, Presentations, Workbooks — when you know what you are looking for but not where it lives.
- What you can see is governed by your project access — if a project is missing, ask an admin to add you on the Team tab.`,
    examples: [
      { verb: 'ask', text: 'Which of my projects have open escalated deal points?' },
      { verb: 'ask', text: 'How do tags affect what the AI can use when drafting?' },
    ],
  },
  {
    id: 'library',
    emoji: '🗄️',
    title: 'The library — every artifact, one place',
    routes: ['^/library', '^/$'],
    keywords: ['library', 'browse', 'all documents', 'presentations', 'workbooks', 'artifacts', 'find', 'across projects', 'filings', 'correspondence'],
    body: `The **library** (sidebar → Library) is the cross-project view of everything you can see, organized by what an artifact **is** rather than where it lives:

- **Documents** — agreements, memos, design docs; every working paper.
- **Presentations** — slide decks, agent-authored or imported.
- **Workbooks** — Excel models, versioned cell-by-cell.
- **Filings & certificates** — executed copies, state filings, issued certificates.
- **Correspondence & sources** — email threads, received originals, exhibits, references.

Every row shows its project, version count, and file type — one click opens the artifact, one click opens its project. Search reaches titles and project names; the kind chips narrow. Superseded drafts stay on their project's Documents tab (they are lineage, not peers).

The sidebar's Documents / Presentations / Workbooks links land here pre-filtered, so "show me every model we have" is one click from anywhere.`,
    examples: [
      { verb: 'ask', text: 'How do I find every workbook across my projects?' },
      { verb: 'ask', text: 'Where do executed copies and certificates live after signing?' },
    ],
  },
  {
    id: 'dashboard',
    emoji: '📊',
    title: 'The project dashboard',
    routes: ['^/deals/[^/]+$'],
    keywords: ['dashboard', 'overview', 'summary', 'workspace', 'navigation', 'job cards', 'jump chips', 'needs you'],
    body: `The dashboard is the project's control room, laid out so the everyday path is one click:

- **Ask pill** (top, under the project name) — one line that expands into the full Ask / Instruct / Add panel on click or **⌘K**.
- **Jump chips** — a sticky "Jump to" row that scrolls to any section of the page (Documents · Draft · Deal points · Structure & facts · Needs you).
- **Job cards** — the four places people actually go: open the draft, review the latest redline, the deal points register, and package export.
- **Main column** — the document tree, the current draft with its version rail, and the deal points board.
- **Deep dive** (collapsible) — company structure map, objectives & purpose audit, key facts, and key information. It remembers whether you keep it open, per project.
- **Needs you rail** (right) — agent questions, AI-suggested deal points, next steps, issues, the activity mini-timeline, comments, and milestones. If it's in the rail, it's waiting on a human.

Tabs (Dashboard · Documents · Deal points · Team · Activity) are in the URL — refresh, the back button, and shared links land on the right tab. Most cards have a small **?** — click it to open this guide at that card's explanation.`,
    examples: [
      { verb: 'ask', text: 'What is still open, and what is each party\u2019s stance?' },
      { verb: 'ask', text: 'Summarize what changed in the latest version' },
      { verb: 'ask', text: 'What needs my attention on this project right now?' },
    ],
  },
  {
    id: 'document-tree',
    emoji: '🌳',
    title: 'Document tree & downloads',
    routes: ['^/deals/[^/]+$'],
    keywords: ['document tree', 'download', 'word', 'pdf', 'key document', 'original file'],
    body: `The document tree shows the project's **key document** as the root, with every other artifact grouped underneath: negotiation record, working documents, filings & certificates, exhibits, references, and sources.

- **Open** views the document in-app; tracked-changes documents open in redline view automatically.
- **Word / PDF** buttons export the latest version, formatted for round-tripping through Microsoft Word.
- **File (original)** appears on uploaded artifacts — it downloads the exact original bytes (including any Word tracked changes), not a regenerated export.

The same project can hold DOCX, PDF and PPTX renditions of one document — the tree shows which is the latest and what kind each file is.`,
    examples: [
      { verb: 'ask', text: 'Which document is the key document here, and what supports it?' },
      { verb: 'ask', text: 'What is the difference between the Word export and the original file download?' },
    ],
  },
  {
    id: 'deal-points',
    emoji: '⚖️',
    title: 'Deal points register',
    routes: ['^/deals/[^/]+$'],
    keywords: ['deal points', 'negotiation', 'stance', 'open items', 'issues', 'compromise', 'lock', 'correspondence', 'accepted', 'rejected', 'decisions', 'undo', 'filter'],
    body: `A **deal point** is one negotiation issue — a clause, a cap, a definition in dispute. The register tracks every point with:

- each party's **stance** (proposed, rejected, agreed) and its history,
- its **decision** — Open, Accepted, Rejected, Countered, Escalated, or Fallen away,
- links to the clauses it anchors to.

Accept or reject a counterparty position, or propose a third version, straight from the board. **Nothing disappears when you decide**: the point stays in place with its new badge, a toast offers **Undo**, and the chip row at the top (**All · Open · Accepted · Rejected · Countered · Escalated · Fallen away**, each with a count) filters the board. The chip you pick is remembered per project. The register updates as new drafts and correspondence are ingested — it is the single source of truth for "what's still open?" *and* "what did we agree to?".

The **Decisions** view lists every accept / reject / counter / status change on record — newest first, with who decided and when; filter to this week or all time, click a row to open the point, and **Export PDF** from that view produces the decisions log. The dashboard trackers ("N accepted · M rejected this week → Decisions") and the kanban column heads deep-link here.

**AI compromise** drafts middle-ground language for a stuck point — it lands as a suggestion on the point for you to adopt, edit, or ignore.

**Lock** freezes an agreed point: the linked clause language is marked frozen, and later drafts that move it get flagged instead of silently absorbed. Unlock when the point re-opens.

**File correspondence** reconciles a pasted email thread or counterparty letter against the register: points it moves are flagged for your review (statuses are never changed for you), and genuinely new issues appear as suggestions to accept or dismiss.

**Verify draft** checks every register point against the current draft and reports per-point evidence — reflected, partial, or missing — so agreed positions can't silently drop out of the document.

**Open-items memo** files a memo document built straight from the register (deterministic — no AI in the loop): every open point with each party's position, plus a settled-items table. It lands on the Documents tab, versioned and exportable like anything else.

**Export PDF** turns the register into a styled negotiation-points document — open and settled items with status chips, every party's current stance in its own colored block, and landing-zone notes in italics — ready to circulate to counsel or include in a package.`,
    examples: [
      { verb: 'ask', text: 'Explain the indemnification point — what does the current draft actually say?' },
      { verb: 'ask', text: 'Is our register position consistent with the current draft?' },
      { verb: 'do', text: 'Propose middle-ground liability-cap language at $2,000,000 and apply it in Section 11' },
      { verb: 'ask', text: 'Which open points have no position from the counterparty yet?' },
      { verb: 'ask', text: 'What did we accept this week, and who accepted it?' },
      { verb: 'do', text: 'Show me the rejected deal points' },
    ],
  },
  {
    id: 'agent-questions',
    emoji: '❓',
    title: 'Agent questions',
    routes: ['^/deals/[^/]+$'],
    keywords: ['agent questions', 'clarification', 'answer', 'apply answer'],
    body: `When the drafting agent hits something ambiguous — an entity it can't resolve, a date that could be two things — it **asks instead of guessing**. Questions appear in the dashboard's Needs-you rail with suggested answers where the AI has good candidates.

Pick a suggestion or type your own answer, then **Apply** — the agent folds the answer into the document as a tracked, revertible edit. Answering questions also teaches the project brain, so future runs won't ask again.`,
    examples: [
      { verb: 'ask', text: 'What open questions does the agent have on this project?' },
      { verb: 'ask', text: 'Why did the agent ask about the governing-law date?' },
    ],
  },
  {
    id: 'key-facts',
    emoji: '🔑',
    title: 'Key facts & key information',
    routes: ['^/deals/[^/]+$', '^/deals/[^/]+/docs/'],
    keywords: ['key facts', 'entities', 'dates', 'amounts', 'addresses', 'audit'],
    body: `The AI parses every document's load-bearing details — **entities, names, dates, amounts, addresses, jurisdictions** — into an auditable list under the dashboard's **Deep dive** section. This exists so that when a document is repurposed (say, one company's operating agreement adapted for another), nothing that obviously needed changing gets missed.

- Facts reflect the **latest version**; superseded values from older versions sit underneath, one click away.
- Each fact can be corrected in place — the change goes through the drafting agent as a tracked edit across the whole document.`,
    examples: [
      { verb: 'ask', text: 'List every dollar amount in the current draft and where it appears' },
      { verb: 'do', text: 'Replace every reference to [old name] with [new name]' },
      { verb: 'ask', text: 'Do any parsed facts conflict between documents in this project?' },
    ],
  },
  {
    id: 'structure-map',
    emoji: '🏢',
    title: 'Company structure map',
    routes: ['^/deals/[^/]+$'],
    keywords: ['structure', 'entities', 'holdco', 'opco', 'diagram', 'org chart'],
    body: `For projects with multiple entity layers (a HoldCo and an OpCo, members, managers, consultants), the structure map — under the dashboard's **Deep dive** section — draws the ownership and management diagram, with **known agreements between entities shown on the connections**.

This is how you can verify the platform actually understands your company before asking it to do something structure-sensitive — like converting a holding-company operating agreement into an OpCo agreement. Uploaded agreements attach to the right edge of the diagram automatically once tagged with their parties.`,
    examples: [
      { verb: 'ask', text: 'Which agreement owns members, capital, and governance — HoldCo or OpCo?' },
      { verb: 'ask', text: 'Map the entities in this project and the agreements between them' },
    ],
  },
  {
    id: 'purpose-audit',
    emoji: '🎯',
    title: 'Objectives & the purpose audit',
    routes: ['^/deals/[^/]+$'],
    keywords: ['objective', 'purpose audit', 'relic', 'misplaced', 'agreement ecosystem', 'gaps'],
    body: `Every project can carry a **stated objective** — the project structure, each agreement's role in it, and what does NOT belong in each document. It lives on the dashboard's Objectives card (under **Deep dive**) and travels with every AI pass on the project.

**Run purpose audit** benchmarks a draft against that objective and the surrounding agreement ecosystem, flagging three kinds of problems:

- **Relics** — language left over from the document this one was adapted from,
- **Misplaced concepts** — provisions that belong in a sibling agreement (e.g. governance terms in an award agreement),
- **Gaps** — things the objective implies that the draft never covers.

Findings link to the exact clauses. Fixes go through the drafting agent as reviewable proposals — the audit never edits anything itself.`,
    examples: [
      { verb: 'ask', text: 'Does this draft match its stated purpose, or is anything misplaced?' },
      { verb: 'do', text: 'Run the purpose-audit fixes: strike the relic clauses and file the edit for review' },
      { verb: 'ask', text: 'What belongs in the OpCo agreement instead of this one?' },
    ],
  },
  {
    id: 'timeline',
    emoji: '🕑',
    title: 'Document timeline & audit trail',
    routes: ['^/deals/[^/]+$'],
    keywords: ['timeline', 'audit', 'history', 'activity'],
    body: `The timeline records everything that happened in the project — versions published, drafts uploaded, deal points updated, agent runs — newest first, with who and when. The dashboard's Needs-you rail carries the recent slice; the **Activity tab** holds the full filterable trail.

For any published version, the **audit trail** expands into the exact changes: which clauses, what text, and why (the instruction or received draft that caused it). Nothing is ever edited in place — every change is a new version with a manifest.`,
    examples: [
      { verb: 'ask', text: 'Who published the last three versions, and what changed in each?' },
      { verb: 'ask', text: 'When did the counterparty\u2019s latest draft arrive and what did it touch?' },
    ],
  },
  {
    id: 'doc-views',
    emoji: '📄',
    title: 'Clean, redline & side-by-side',
    routes: ['^/deals/[^/]+/docs/'],
    keywords: ['redline', 'clean', 'side-by-side', 'view mode', 'compare', 'tracked changes'],
    body: `Every document can be read three ways, switchable at the top of the page:

- **Clean** — the current text as it would be signed.
- **Redline** — insertions and deletions against a base version, with the reason for each change alongside. Pick any base version to compare against.
- **Side-by-side** — old and new versions in parallel columns.

Documents that arrived as Word tracked-changes files open in redline automatically. Redlines here are real: exports carry genuine Word tracked changes, not colored text.

Excel workbooks get their own view — see the **Excel workbooks** topic.`,
    examples: [
      { verb: 'ask', text: 'What is the difference between clean and redline views?' },
      { verb: 'ask', text: 'Summarize what the counterparty changed in their latest turn, clause by clause' },
    ],
  },
  {
    id: 'workbooks',
    emoji: '📈',
    title: 'Excel workbooks — models as first-class documents',
    routes: ['^/deals/[^/]+/docs/', '^/deals/[^/]+$'],
    keywords: ['workbook', 'excel', 'xlsx', 'model', 'formula', 'audit model', 'spreadsheet', 'cells', 'financial model', 'sensitivity', 'assumptions', 'dependency', 'house style'],
    body: `Excel workbooks are first-class documents, not attachments: upload an .xlsx and the **entire model** — every sheet, cell, formula, format, merge, and named range — is captured and versioned exactly like an agreement. What that unlocks:

**Understanding.** On ingest the platform studies the model: it classifies each sheet's role (inputs, calculations, outputs, data), maps the **sheet dependency graph** (which tabs feed which), detects **model smells** — hardcoded constants buried inside formulas, volatile functions, a formula pattern that breaks mid-row — and profiles the model type (3-statement, DCF, cap table…), key drivers, and outputs. The whole model is also indexed into the project brain, so Ask can answer "which cells drive 2027 EBITDA?" with sheet-and-cell citations.

**Editing by instruction.** The **workbook agent** takes plain language — "raise H2 opex 10% and flag the assumptions it touches", "add a churn sensitivity table" — and publishes structured, versioned cell edits. Every version carries a **cell-level manifest** (before/after per cell), rendered in the Changes tab like a document redline; any two versions diff cell-by-cell. Upload a counterparty's revised model and the diff shows exactly what moved.

**Building from scratch.** Draft with AI (Documents tab) has a **Workbook** kind: describe the model in a brief and the agent builds a new investor-grade workbook — assumptions, calculation, and output tabs properly separated — as a versioned project document.

**The house style.** Downloads are authored fresh from the model in institutional conventions — blue inputs, black formulas, green cross-sheet links, navy headers, double-rule totals — with full recalculation on open. A real model a fund's analysts would recognize, not a static export.

**The audit.** **Audit model** runs the full review: the deterministic checks above plus an AI pass on assumption reasonableness, internal consistency, and tie-outs. The result files as a **memo document** in the project with a severity-ordered fix list — versioned, exportable, and circulate-ready.

**The view.** The workbook view is a sheet-tabbed grid with house-style hints, changed-cell highlights, a **formula toggle** (values or formulas), the version rail, and the model profile card. Tabs and cells are deep-linkable, and the voice agent uses that to give tab-by-tab walkthroughs.`,
    examples: [
      { verb: 'do', text: 'Raise H2 operating expenses by 10% and flag every assumption the change touches' },
      { verb: 'do', text: 'Audit this model — structure, assumptions, and tie-outs — and file the memo' },
      { verb: 'do', text: 'Build a 3-year operating model for a SaaS business — assumptions, P&L, and a summary tab' },
      { verb: 'ask', text: 'Which cells drive the 2027 EBITDA number in this workbook?' },
      { verb: 'ask', text: 'What model smells did the audit find, and which are severe?' },
    ],
  },
  {
    id: 'slide-decks',
    emoji: '📽️',
    title: 'Slide decks & presentations',
    routes: ['^/deals/[^/]+/docs/', '^/deals/[^/]+$'],
    keywords: ['deck', 'slides', 'presentation', 'pptx', 'powerpoint', 'convert', 'speaker notes'],
    body: `Presentations are project documents too — a deck's slides are versioned and edited exactly like an agreement's clauses.

- **Draft with AI → Presentation** builds a full 16:9 deck from a brief: title slide, section dividers, content layouts, bullets, and speaker notes.
- **Convert to presentation** (document page) distills an existing agreement or memo into a deck — the agent picks the story arc and keeps every claim traceable to the source document.
- The agent edits decks by instruction ("tighten the ask slide, add a risks slide before the close"), filing reviewable proposals with the same manifest discipline as documents.
- **PPTX export** produces a real PowerPoint file — native shapes and text, not images — that round-trips through PowerPoint.
- The **PowerPoint add-in** connects a deck open in PowerPoint to its project: pull the latest version, edit with PowerPoint's tools, publish back as a new version.`,
    examples: [
      { verb: 'do', text: 'Convert this agreement into a 10-slide briefing deck for the board' },
      { verb: 'do', text: 'Add a deal-timeline slide after the overview and tighten the ask slide' },
      { verb: 'ask', text: 'Which slide covers the liability cap, and does it match the current draft?' },
    ],
  },
  {
    id: 'word-addin',
    emoji: '🧩',
    title: 'The Word add-in',
    routes: ['^/deals/[^/]+/docs/'],
    keywords: ['word add-in', 'office', 'task pane', 'pull', 'publish', 'content controls', 'microsoft word'],
    body: `For people who live in Microsoft Word, the fast.legal add-in puts the project inside Word's task pane — same login, same permissions as the app.

- **Pull** any document version into Word; clauses map to content controls so the platform always knows which clause you are editing.
- Edit with Word's own tools — then **publish back** as a new version; the manifest records what changed like any other edit.
- **Deal-point navigation** in the pane jumps to the clause a register point anchors to, with the point's stance summary alongside.

The round-trip is lossless in both directions: exports carry genuine Word formatting and tracked changes, and an edited file publishes back without disturbing untouched clauses.`,
    examples: [
      { verb: 'ask', text: 'How do I edit this document in Microsoft Word and publish my changes back?' },
      { verb: 'ask', text: 'Will Word tracked changes survive the round-trip into the platform?' },
    ],
  },
  {
    id: 'versions',
    emoji: '🧬',
    title: 'Versions, lineage & immutability',
    routes: ['^/deals/[^/]+/docs/'],
    keywords: ['version', 'lineage', 'immutable', 'checkpoint', 'hash', 'revert', 'history'],
    body: `Documents never change in place. Every edit — human or agent — publishes a **new version** with a manifest of exactly what changed and why. The version rail shows the full lineage, latest first.

- Versions are **immutable checkpoints** — each is content-hashed, so you can prove what the document said at any moment.
- Any version can be opened, compared against, exported, or **reverted to** (the revert itself is just another tracked version).
- A document that started life as another company's agreement keeps that provenance in its lineage — the original is tagged as the source baseline.
- Coming back after time away? The document remembers the last version **you** saw and shows a "published since you last viewed" banner with who published what — one click for an AI summary or the changes manifest.
- **Changes memo** files a narrated memo of the document's recorded edits, version by version, grouped by theme — grounded entirely in the manifests, landed as a project document you can export and circulate.`,
    examples: [
      { verb: 'ask', text: 'What changed between v3 and the current version, and why?' },
      { verb: 'do', text: 'File a changes memo covering everything since the counterparty\u2019s last turn' },
    ],
  },
  {
    id: 'drafting-agent',
    emoji: '✍️',
    title: 'The drafting agent — vibe & precision',
    routes: ['^/deals/[^/]+/docs/', '^/deals/[^/]+$'],
    keywords: ['drafting agent', 'vibe', 'precision', 'proposal', 'manifest', 'edit', 'instruction', 'whole project'],
    body: `Tell the agent what to change in plain language — "tighten the indemnity cap to $2M", "replace every reference to the old entity name". Two modes:

- **Precision mode** (default) — the agent files a **proposal**: a reviewable list of every change with before/after. You tick the ones you want, approve, and it publishes as a new version. You can also send it back with notes for revision.
- **Vibe mode** — the agent applies the edit immediately and publishes a version. Still fully tracked and revertible; just skips the review step.

Either way, every change lands in the version's **manifest** — the permanent record of what changed, why, and which instruction caused it.

The agent also protects you from wasted turns:

- **Already in the draft** — if what you asked for is already in the current version (it landed in an earlier turn, or someone else applied it), the agent says so with the evidence instead of filing a duplicate proposal. Individual changes that are already present get skipped the same way.
- **Pasted emails** — paste a whole email thread as the instruction and the agent extracts the actual drafting asks first (the full paste is preserved on the proposal record for the audit trail).
- **Stale proposals** — if the draft moves after a proposal is filed, the proposal is marked **stale** with one click to **recompute against the current draft**; the stale one is retired automatically. Pending proposals are also badged on the project's Documents tab so reviews don't rot unseen.

**Whole-project instructions** — pick **All documents** as the Instruct target and the agent routes one instruction across the project, filing one reviewable proposal per affected document. Always precision mode. Worked example:

1. Open the project's Ask panel (⌘K), switch to **Instruct**.
2. In the document picker choose **All documents (route across the project)**.
3. Type: "Conform the SOW's IP-ownership language to the MSA's new Section 12 — Customer owns deliverables, Vendor keeps pre-existing IP."
4. The agent plans targets (here: SOW, maybe the MSA glossary), files one proposal per touched document, and reports documents where the language is **already in the draft**.
5. Review each proposal on its document page — tick, apply, done. Each publishes its own version with its own manifest.`,
    examples: [
      { verb: 'do', text: 'In §12.5 keep the AI-output indemnity carve-out, and add that Customer validates outputs before commercial use' },
      { verb: 'do', text: 'Add a Change of Control definition to the assignment section' },
      { verb: 'do', text: 'Target "All documents": conform the SOW to the MSA\u2019s IP clause' },
      { verb: 'ask', text: 'Is the $2M liability cap already in the current draft?' },
    ],
  },
  {
    id: 'create-documents',
    emoji: '🪄',
    title: 'Create, convert & derive documents',
    routes: ['^/deals/[^/]+$', '^/deals/[^/]+/docs/'],
    keywords: ['create', 'draft with ai', 'new document', 'deck', 'presentation', 'derive', 'convert', 'distill', 'brief', 'from scratch'],
    body: `The agent doesn't just edit — it authors. Three ways to make something new:

- **Draft with AI** (Documents tab) — describe a document, presentation, or workbook in a brief and the agent drafts the whole thing: a full agreement with recitals and defined terms, a 16:9 deck with title slide, dividers, bullets and speaker notes, or a model scaffold. It lands as a versioned project document you iterate on like any other.
- **Convert to presentation** (document page) — distill an existing document into a deck; the agent picks the story arc and keeps every claim traceable to the source.
- **Derive** (document page) — refactor an agreement for a related entity: HoldCo operating agreement → subsidiary OA, one state's employment agreement → another's. You get the clean derived draft plus a redline against the source with **per-change rationale**, and the lineage records where it came from.

Everything created this way inherits the project's context — the structure map, key facts, and sibling agreements — so a derived OpCo agreement knows which concepts stay at the HoldCo.`,
    examples: [
      { verb: 'do', text: 'Draft a mutual NDA for [company] under Delaware law, 2-year term, standard carve-outs' },
      { verb: 'do', text: 'Convert this agreement into a 10-slide briefing deck for the board' },
      { verb: 'do', text: 'Derive an OpCo operating agreement from this HoldCo agreement — governance stays at HoldCo' },
    ],
  },
  {
    id: 'uploads',
    emoji: '📥',
    title: 'Uploading drafts & related documents',
    routes: ['^/deals/[^/]+$', '^/deals/[^/]+/docs/'],
    keywords: ['upload', 'new draft', 'ingest', 'counterparty', 'articles', 'certificates', 'tags', 'folder', 'zip', 'bulk', 'many files', 'batch'],
    body: `Add a new document from the project card (**Add** next to Ask / Instruct) or **Add document** on the Documents tab: drop a file at the top, or paste an email thread. **Add & import** only lights up after a file is attached or text is pasted. The document is ingested, graphed, and indexed into this project's brain.

**Many files at once**: drop several files, a whole **folder**, or a **.zip** onto the zone (or use **Choose a folder…**). Folders are walked and zips unpacked in your browser; every supported file (.docx, .pdf, .xlsx, .txt, .rtf, .md, .eml) becomes its own document and they all import together. Anything unsupported is counted as "skipped" so nothing disappears silently. A title you type is used as a prefix when several files are added.

Upload a counterparty's new draft of an *existing* document on the document page (**New draft**) — attach the DOCX and PDF together if you have both, and paste any context that came with it (your lawyer's notes, the cover email). The AI aligns it with the working draft so the redline against your last version is meaningful.

Related documents — articles of organization, filing certificates, signed side agreements — also upload from the Documents tab. **Tag them well** (original, filing, exhibit, correspondence…): tags drive where they appear in the document tree and how the AI relates them to the core document.

Busy projects can also file paper into **folders** — the convention is **Internal** (memos, positions, working redlines) vs **External** (sent to or received from the counterparty), plus any custom label. Use **Folder…** on a document row; folder chips at the top of the Documents tab filter every section.

Everything uploaded is embedded into the project brain, so the next Ask or Instruct knows about it.`,
    examples: [
      { verb: 'ask', text: 'How do I upload the counterparty\u2019s new draft so the redline lines up?' },
      { verb: 'ask', text: 'What happens to a pasted email thread when I add it to the project?' },
    ],
  },
  {
    id: 'smart-import',
    emoji: '🧲',
    title: 'Smart import & guest drop links',
    routes: ['^/deals/[^/]+$'],
    keywords: ['smart import', 'profile', 'suggested points', 'tracked changes', 'guest', 'drop link', 'inbound', 'classify'],
    body: `Ingest is not just parsing — it studies what arrived:

- **Document profile** — every negotiated document gets profiled on import: parties, governing law, term, key numbers, and how it relates to documents already in the project.
- **Suggested deal points** — smart import proposes 5–12 material points for the register (caps, indemnities, exclusivity, unusual terms). They land as **suggestions** — you accept each into the register or dismiss it; nothing is tracked without your say-so.
- **Tracked changes recognized** — a Word file with tracked changes ingests as a real redline turn: the markup becomes the diff against your last version, attributed to the sending party.
- **Auto-classification** — related papers (articles, filings, exhibits, correspondence) are classified against the working documents and slotted into the right branch of the document tree.
- **Guest drop links** — outside parties can send you files of any size through an expiring account-free link; what arrives lands in the project ingest queue like any upload.

The dashboard's Needs-you rail shows suggested points awaiting review; the register's suggested row is the same queue.`,
    examples: [
      { verb: 'ask', text: 'What did smart import flag on the document that just arrived?' },
      { verb: 'ask', text: 'Profile the counterparty\u2019s draft — parties, governing law, key numbers, unusual terms' },
    ],
  },
  {
    id: 'exports',
    emoji: '📤',
    title: 'Exports — Word, PDF, PPTX, packages',
    routes: ['^/deals/[^/]+/docs/', '^/deals/[^/]+$'],
    keywords: ['export', 'download', 'docx', 'pptx', 'tracked changes', 'original file', 'package', 'share link', 'letterhead'],
    body: `From any document you can export:

- **Word (clean)** — the current version, precisely formatted for editing in Microsoft Word.
- **Word (redline)** — genuine Word tracked changes against any base version, ready for opposing counsel.
- **PDF** — clean, or an annotated redline with change reasons in the margin.
- **PPTX** — for presentation documents.
- **Original file** — the exact bytes of an uploaded artifact, untouched.

For a whole response package — clean copies, redlines, and the negotiation-points PDF in one zip — use **Export package** on the project's Documents tab: tick the documents, pick clean/redline per document and Word/PDF format, and download once. Each file in the zip is identical to its single-export button. The **Folders** picker controls the layout inside the zip: Auto groups clean/ and redlines/ subfolders once the package is big enough to benefit, or choose flat, clean/redlines, or one folder per document.

**Letterhead clean copies** renders the clean Word copies on your organization's letterhead theme — band, legend, linked contents table, branded footer (admins set the theme under Team → Letterhead).

**Copy share link** builds the package once and gives you an expiring link (7 days) anyone can download without an account — the package is frozen at creation, so new versions need a new link. Every download is recorded in the project's activity.

The deal points register exports too: **Export PDF** on the register produces the styled stance-by-party negotiation points document (status chips, each party's current position, landing-zone notes).

**Draft email** (next to Export package) writes a send-ready email from the live deal state — internal update, counsel briefing, or a counterparty note (counterparty drafts use only circulated facts; internal stances are withheld). Nothing sends automatically: copy the subject and body into your mail client.

Exports round-trip: a Word file you export, edit, and upload back ingests as a new version with the differences tracked.`,
    examples: [
      { verb: 'ask', text: 'How do I export a package — clean copy, redline, and open points in one download?' },
      { verb: 'ask', text: 'How do I share a package with an expiring link, or export on letterhead?' },
      { verb: 'do', text: 'Draft a counterparty email summarizing our response package' },
    ],
  },
  {
    id: 'envelopes',
    emoji: '✒️',
    title: 'E-signature — envelopes & the signing ceremony',
    routes: ['^/deals/[^/]+$', '^/sign/'],
    keywords: ['signature', 'esign', 'envelope', 'sign', 'ceremony', 'otp', 'executed', 'certificate of completion', 'evidence chain'],
    body: `fast.legal signs documents in-app — no DocuSign detour — and every signature is pinned to the exact sealed version it covers.

- **Request signatures** (Documents tab) bundles one or more documents into an **envelope**: pick signers and order, and the platform places signature/initial/date fields at the signature blocks it detects (adjustable).
- **Signers need no account.** Each gets an emailed link → verifies with a one-time email code → walks a guided ceremony over the real Word print layout → adopts a signature and signs. Sequential routing notifies the next signer automatically.
- **What is signed is provably what was reviewed** — the envelope pins each document's sealed version checkpoint hash; a draft that moves after sending can't silently swap in.
- **Completion** produces an executed PDF per document — script-font signatures at their anchored clauses plus a **Certificate of Completion** annex (verification, consent, IP, per-signer evidence hashes) — and emails copies to everyone. The executed artifacts file into the document tree.
- Every signature seals into a per-envelope **SHA-256 evidence chain**, and checkpoint + signature hashes are **anchored on-chain** — the certificate footer carries the transaction for independent verification.

Track progress on the Documents tab envelope card (viewed / signed chips per signer), resend a fresh link, or void the envelope at any point before completion.`,
    examples: [
      { verb: 'ask', text: 'How do I send this agreement for signature, and what do signers see?' },
      { verb: 'ask', text: 'What proves the signed document matches the version we reviewed?' },
      { verb: 'ask', text: 'Where is the executed copy and the certificate of completion?' },
    ],
  },
  {
    id: 'certificates',
    emoji: '📜',
    title: 'Equity award certificates',
    routes: ['^/deals/[^/]+$'],
    keywords: ['certificate', 'equity', 'award', 'grant', 'units', 'issue'],
    body: `For equity grants that already live in a project (an award agreement, a unit grant), **Issue equity certificate** (Documents tab) renders the celebratory artifact: a branded certificate with the company mark, holder, units/class, dates, and certificate number.

- Fields prefill from the award documents in the project; adjust and issue.
- The certificate is rendered server-side, **filed into the document tree** under filings & certificates, and downloaded — versioned and exportable like everything else.
- Branding follows the issuing company's letterhead theme.

The certificate is evidence of a grant recorded elsewhere — the agreement stays the legal source of truth; the certificate is the artifact you hand the holder.`,
    examples: [
      { verb: 'ask', text: 'How do I issue an equity certificate for this award, and what does it prefill from?' },
      { verb: 'ask', text: 'Which documents in this project record the grant the certificate certifies?' },
    ],
  },
  {
    id: 'jurisdictions',
    emoji: '🌐',
    title: 'Jurisdiction screens',
    routes: ['^/deals/[^/]+/docs/[^/]+/compliance', '^/deals/[^/]+/docs/'],
    keywords: ['jurisdiction', 'compliance', 'screen', 'enforceability', 'non-compete', 'statute', 'panel', 'state law'],
    body: `Every document has a **Jurisdictions** page (button on the document page, or /compliance on the URL): an AI screen of the current draft against a panel of jurisdictions — NV, DE, TX, CA, NY, Cayman, EU, US federal, or any jurisdiction you type in.

- Each finding names the **jurisdiction, severity, clause, and issue**, with suggested language where the fix is drafting (not business judgment).
- **Propose these fixes** hands the findings to the drafting agent as one instruction — you get a reviewable proposal, per changed section, like any other edit.
- Screens are **versioned history**: each run records the draft version it screened, so you can show a clause was checked against Minnesota law before it went out.
- The same loop works from ⌘K: name a jurisdiction in an Ask ("screen the restrictive covenants for Minnesota enforceability") and the screen folds into the conversation; Instruct then files the compliant edit.

The screen is an issue-spotter grounded in statute passages — counsel judgment stays with you; the platform makes the checking and the paper trail cheap.`,
    examples: [
      { verb: 'ask', text: 'Screen the restrictive covenants for Minnesota enforceability and propose compliant language' },
      { verb: 'do', text: 'Make the post-employment non-compete compliant with Minnesota law and file a reviewable edit' },
      { verb: 'ask', text: 'Which clauses would a California court likely refuse to enforce?' },
    ],
  },
  {
    id: 'brain',
    emoji: '🧠',
    title: 'The project brain',
    routes: ['^/deals/[^/]+'],
    keywords: ['brain', 'chat', 'ask', 'knowledge', 'retrieval', 'citations', 'scope'],
    body: `Every project has a **brain** — an embedded index of all its documents (clause by clause), deal points, version analyses, and filed correspondence, woven into a knowledge graph.

Open the chat bubble (bottom right) or press **⌘K** and ask anything: "what's still open in this negotiation?", "who asked for the warranty change?". Answers cite the exact clauses and link to them.

Scopes: **this project**, **related projects** (same tags — useful when a company has several), or the **whole org**. The brain retrains itself whenever the project changes.`,
    examples: [
      { verb: 'ask', text: 'What is still open in this negotiation, and who moved last on each point?' },
      { verb: 'ask', text: 'Who asked for the warranty change, and in which turn?' },
      { verb: 'ask', text: 'Produce a market study on whether the post-employment non-compete is in line with market, then recommend edits' },
    ],
  },
  {
    id: 'command-bar',
    emoji: '⌨️',
    title: 'Ask, instruct & add (⌘K)',
    routes: ['^/deals/'],
    keywords: ['command bar', 'cmd+k', 'shortcut', 'ask', 'instruct', 'natural language', 'project', 'upload', 'add document'],
    body: `Inside a project the **Ask, instruct & add** panel lives on the project page itself — collapsed to a one-line pill under the project name so the tabs stay visible; it expands on click or **⌘K** (Ctrl+K on Windows). Outside a project, ⌘K opens a floating palette for platform / knowledge questions.

Three verbs:

- **Ask** — a question, answered by **this project's brain**, with citations you can click through to the clause or document. Outside a project it answers from the knowledge base and this guide.
- **Instruct** — a drafting instruction in plain language. The agent studies a document **in this project** and files a **precision proposal** — you review every proposed change before anything is published. Nothing is ever applied without your approval. You can also **paste a whole email thread**: the agent extracts the drafting instructions from it (and keeps the full paste on the proposal record). If what you asked for is already in the current draft, it tells you so — with evidence — instead of filing a duplicate.
- **Add** — drop a file or paste an email into this project. The drop zone is at the top; **Add & import** only highlights after a file is attached or pasted text is present. The document is ingested, graphed, and indexed into the project brain.

Worked example (inside a project):

1. Open the project. The pill sits under the title — click it or press ⌘K. Leave **Ask** selected.
2. Type: "What is still open on indemnification and the liability cap?"
3. The answer fills the panel; source chips under it jump to the cited clause in this project.
4. Switch to **Instruct**, pick the document if asked, and type a precise edit.
5. When the proposal lands, click **Review on…** — tick the ops you want, then Apply.
6. Switch to **Add** to file a new draft or a pasted email into the same project.

How counsel actually works (the same loop you would run with an assistant):

1. **Ask** — explain a deal point, check a stance against the draft, ask what is still open, or propose compromise language. Follow-ups stay in the thread.
2. **Deal points** — record the stance you just agreed (accept / reject / compromise). Ask does not write the register.
3. **Instruct** — “In §12.5 keep the carve-out and add…” files a reviewable proposal. Nothing publishes without your approval.
4. **Add** — drop the counterparty turn or paste the cover email so the next Ask is context-aware.

Ask and Instruct are multi-document: they know which agreement owns a concept (HoldCo vs OpCo, award vs operating agreement). Ask will name the home document. Instruct will refuse to write a sibling-owned concept into the wrong draft.

**Market study:** Ask “produce a market study on whether X is in line with market.” The app files an analyst memo in the project (project facts + statute screen + labeled comparables — not a commissioned survey). Then **Instruct from this conversation** or **Incorporate study into the draft** files a reviewable edit. Apply it, then download **Word (redline)** for tracked changes.

**Ask → Instruct:** the conversation stays on the project. As it evolves, Instruct uses the whole thread (not just the last question) so counsel becomes an edit.

Name a **jurisdiction** to fold a statute screen into the same loop. Same **Propose these fixes** lives on the document’s Jurisdictions page.

Enter submits; Shift+Enter adds a line.`,
    examples: [
      { verb: 'ask', text: 'What is still open on indemnification and the liability cap?' },
      { verb: 'do', text: 'Paste an email thread here — the agent extracts the drafting instructions from it' },
      { verb: 'ask', text: 'How do I file correspondence against the register, or verify the draft reflects it?' },
    ],
  },
  {
    id: 'voice-agent',
    emoji: '🎙️',
    title: 'The voice agent',
    routes: ['^/deals/', '^/$'],
    keywords: ['voice', 'microphone', 'hands-free', 'speak', 'realtime', 'talk'],
    body: `The **Voice** button (bottom right) opens a hands-free assistant: talk to the platform the way you would brief a colleague — "open the Meridian project", "what's still open on the MSA?", "walk me through what changed this week".

- It can **navigate for you** — opening projects, documents, and views as it answers — and points at what it's describing.
- It runs on the same project brain as ⌘K Ask, so answers carry the same citations and respect the same project access.
- **Its tools are permission-capped at the platform level**: read-only queries plus at most creating suggestions. The voice agent cannot publish versions, change register statuses, or send anything — by construction, not by prompt.
- The panel includes a text input too — type instead of speaking and the same tools answer.

Use it for meeting prep ("brief me on this project"), live walkthroughs, or working through a document when your hands are on other things.`,
    examples: [
      { verb: 'ask', text: 'Brief me on this project like I\u2019m walking into the negotiation in ten minutes' },
      { verb: 'ask', text: 'What changed across my projects this week?' },
    ],
  },
  {
    id: 'team-roles',
    emoji: '👥',
    title: 'Team, roles & powers',
    routes: ['^/deals/[^/]+$', '^/team'],
    keywords: ['team', 'roles', 'invite', 'permissions', 'viewer', 'editor', 'admin', 'powers', 'restricted', 'can invite'],
    body: `Access is per-project. Org **admins** manage people; each project participant has a role that maps to powers:

- **Viewer** — read and follow the project.
- **Commenter** — plus discussion threads.
- **Editor** — plus uploads, edits, and agent requests.
- **Approver** — plus approving agent proposals and deal-point stances.
- **Admin** — plus managing the project's people.

Powers can be fine-tuned per person (e.g. allow download but not upload). Invite from the Team tab or page — invitees get a link that pre-fills their email; they only choose a password and land inside the right projects automatically.

Two org-level switches (People & Access): **can invite** lets a member invite outside emails onto projects where they are a project admin, without seeing anything else; and a project's **Restricted** toggle (Team tab) hides it from org admins who were not added to it — only the org owner always sees everything.

**Support access** (Team page → Support access): fast.legal staff can never open your content by default. If you want help with something, an org admin grants a time-boxed, read-only window — scoped to the whole org or one project, 48 hours unless you choose otherwise (owners may grant until revoked) — and can revoke it any time. Every support read is logged in your Activity feed as "under a grant by <admin>". Email support@fast.legal first; they will tell you if a grant is needed.`,
    examples: [
      { verb: 'ask', text: 'Who can approve agent proposals on this project?' },
      { verb: 'ask', text: 'How do I invite outside counsel with download-only access?' },
    ],
  },
  {
    id: 'billing',
    emoji: '💳',
    title: 'Plans, trials & billing',
    routes: ['^/pricing', '^/billing'],
    keywords: ['billing', 'plan', 'trial', 'seats', 'tokens', 'upgrade', 'price', 'subscription', 'token pack', 'enterprise', 'team plan', 'starter'],
    body: `Billing is per **organization**. Every new workspace starts on a free time-boxed trial — 14 days of Team features with an AI token budget, no card. Some customers are set up by us on a longer or larger trial (for example an Enterprise trial); the plan, days left, seats and token allowance always show on **Pricing** (sidebar → Billing).

Plans: **Starter** (1 seat, 5 projects), **Team** (5 seats, 25 projects), **Enterprise** (15 seats, unlimited projects, the largest monthly AI allowance; custom above that). A plan is a recurring license billed monthly or annually (20% off); converting during a trial carries the remaining trial days into checkout. Busy months are covered by one-time **token packs** — never a surprise overage.

When a trial ends without a plan, the workspace drops to a view-your-work floor: everything you created stays readable, but exports, AI actions, comparisons and add-ins wait for a plan (nothing is deleted). Only org owners and admins can change the plan or open the billing portal.`,
    examples: [
      { verb: 'ask', text: 'How many days are left on our trial and what does it include?' },
      { verb: 'ask', text: 'What happens to our documents if the trial ends?' },
    ],
  },
  {
    id: 'cockpit',
    emoji: '🛰️',
    title: 'The live cockpit',
    routes: ['^/activity'],
    keywords: ['cockpit', 'activity', 'trace', 'graph', 'vector', 'progress', 'eta'],
    body: `Long-running agent tasks — document refactors, large ingests — stream a **live cognitive trace** to the cockpit: every graph-database hop and vector-database retrieval the agent makes, in real time, with elapsed time and cost.

Use it to watch a run live, audit what the agent consulted afterward, or diagnose why something took the path it did. Every run leaves a permanent trace and audit file in the project.`,
    examples: [
      { verb: 'ask', text: 'What did the last agent run on this project consult, and what did it cost?' },
    ],
  },
];

/* Glossary — dotted-underline terms across the UI. Each entry names the
   guide topic that explains it fully. */
export const GLOSSARY = {
  redline: {
    def: 'A comparison view marking every insertion and deletion against a base version — exported as genuine Word tracked changes.',
    topic: 'doc-views',
  },
  manifest: {
    def: 'The permanent record attached to every version: exactly which clauses changed, the before/after text, and the instruction that caused it.',
    topic: 'versions',
  },
  'precision mode': {
    def: 'The agent proposes changes as a reviewable list; nothing is published until you approve.',
    topic: 'drafting-agent',
  },
  'vibe mode': {
    def: 'The agent applies your instruction immediately as a new tracked version — faster, still fully revertible.',
    topic: 'drafting-agent',
  },
  lineage: {
    def: 'The full ancestry of a document: every version, what it was based on, and where it originally came from.',
    topic: 'versions',
  },
  'checkpoint hash': {
    def: 'A content fingerprint sealed into each version, proving what the document said at that moment.',
    topic: 'versions',
  },
  'deal point': {
    def: 'One negotiation issue — a clause or term in dispute — tracked with each party\u2019s stance and status.',
    topic: 'deal-points',
  },
  'project brain': {
    def: 'The project\u2019s embedded knowledge index — every clause, deal point and correspondence — that powers Q&A and agent retrieval.',
    topic: 'brain',
  },
  'key document': {
    def: 'The primary working document of a project — the root of the document tree; everything else supports it.',
    topic: 'document-tree',
  },
  envelope: {
    def: 'A signature request bundling documents (pinned at exact sealed versions), signers, and fields — the unit of e-signing.',
    topic: 'envelopes',
  },
  'evidence chain': {
    def: 'The per-envelope SHA-256 chain sealing every signature\u2019s who/what/when — anchored on-chain for independent verification.',
    topic: 'envelopes',
  },
  'purpose audit': {
    def: 'An AI benchmark of a draft against the project\u2019s stated objective — flags relics, misplaced concepts, and gaps.',
    topic: 'purpose-audit',
  },
  'workbook model': {
    def: 'The captured representation of an Excel file — every sheet, cell, formula, format, and named range — versioned per document version.',
    topic: 'workbooks',
  },
  'model smells': {
    def: 'Mechanical model-quality findings: hardcoded constants inside formulas, volatile functions, formula patterns that break mid-row.',
    topic: 'workbooks',
  },
  'house style': {
    def: 'Institutional workbook conventions applied on download — blue inputs, black formulas, green links, navy headers, double-rule totals.',
    topic: 'workbooks',
  },
};

export function getTopic(id) {
  return GUIDE_TOPICS.find((t) => t.id === id) || null;
}

/* Topics relevant to a pathname, most specific first; the rest follow so the
   panel always shows the full guide. */
export function topicsForPath(pathname) {
  const here = [];
  const rest = [];
  for (const t of GUIDE_TOPICS) {
    const match = (t.routes || []).some((r) => new RegExp(r).test(pathname));
    (match ? here : rest).push(t);
  }
  return { here, rest };
}

/* Copy-pasteable agent requests for a route — the command bar derives its
   suggestion chips from these (one source of truth for "how do I ask").
   verb: 'ask' | 'do' matching the command bar's verbs. */
export function examplesForPath(pathname, verb) {
  const { here } = topicsForPath(pathname);
  const out = [];
  for (const t of here) {
    for (const ex of t.examples || []) {
      if (ex.verb === verb && !out.includes(ex.text)) out.push(ex.text);
    }
  }
  return out;
}
